/**
 * HYPSEYA — Admin Analytics Dashboard
 * Route: /admin/analytics  (password-protected — same ADMIN_SECRET as AdminUsers)
 * Shows: DAU/WAU/MAU, retention, ERI trends, sessions, clinical outcomes.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Tiny sub-components ────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = '#1E6B68', highlight = false }) {
  return (
    <div style={{
      background: highlight
        ? `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`
        : 'rgba(255,255,255,0.85)',
      borderRadius: '14px',
      padding: '16px 18px',
      border: `1px solid ${highlight ? color + '30' : 'rgba(184,211,207,0.35)'}`,
      minWidth: 0,
    }}>
      <div style={{ fontSize: '0.62rem', color: '#9AADAB', textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: '700', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: '700', color: highlight ? color : '#1E3A39', fontFamily: "'Playfair Display', serif", lineHeight: 1.1 }}>
        {value ?? '—'}
      </div>
      {sub && <div style={{ fontSize: '0.7rem', color: '#9AADAB', marginTop: '3px' }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: '0.65rem', color: '#9AADAB', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: '700', margin: '28px 0 12px' }}>
      {children}
    </div>
  );
}

function RetentionBar({ label, value, target }) {
  const pct = value ?? 0;
  const color = pct >= target ? '#2D8B87' : pct >= target * 0.6 ? '#F0A500' : '#B8906A';
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '0.78rem', color: '#555' }}>{label}</span>
        <span style={{ fontSize: '0.78rem', fontWeight: '700', color }}>
          {value !== null && value !== undefined ? `${value}%` : 'Not enough data'}
        </span>
      </div>
      <div style={{ height: '6px', background: 'rgba(184,211,207,0.3)', borderRadius: '3px' }}>
        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: '3px', transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ fontSize: '0.65rem', color: '#B8D3CF', marginTop: '2px' }}>Target: {target}%</div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(184,211,207,0.5)', borderRadius: '10px', padding: '8px 12px', fontSize: '0.78rem', color: '#333', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: '600', marginBottom: '4px' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>{p.name}: {p.value}</div>
      ))}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
export default function AdminAnalytics() {
  const [secret, setSecret]   = useState('');
  const [authed, setAuthed]   = useState(false);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async (s = secret) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/analytics?secret=${s}`);
      setData(res.data);
      setAuthed(true);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.response?.status === 403 ? 'Wrong secret.' : 'Server error — is the backend running?');
    } finally {
      setLoading(false);
    }
  };

  // Auth gate
  if (!authed) return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 100%)',
      padding: '32px',
    }}>
      <div style={{ fontSize: '1.1rem', fontFamily: "'Playfair Display', serif", color: '#1E3A39', marginBottom: '24px' }}>
        HYPSEYA Analytics
      </div>
      <input
        type="password"
        value={secret}
        onChange={e => setSecret(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && load()}
        placeholder="Admin secret"
        style={{
          padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem',
          border: '1.5px solid rgba(184,211,207,0.6)', outline: 'none',
          marginBottom: '12px', width: '260px', textAlign: 'center',
        }}
      />
      {error && <div style={{ color: '#B8906A', fontSize: '0.82rem', marginBottom: '12px' }}>{error}</div>}
      <button
        onClick={() => load()}
        disabled={loading}
        style={{
          padding: '12px 32px', borderRadius: '12px', border: 'none',
          background: '#1E6B68', color: '#fff', fontWeight: '600',
          fontSize: '0.9rem', cursor: 'pointer',
        }}
      >
        {loading ? 'Loading…' : 'Enter →'}
      </button>
    </div>
  );

  const { users, engagement, retention, eri, clinical, content } = data || {};

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 60%, rgba(184,211,207,0.12) 100%)',
      padding: '28px 24px 80px', maxWidth: '900px', margin: '0 auto',
    }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
        <div>
          <h1 style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', color: '#1E3A39', fontWeight: '500' }}>
            Analytics
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#9AADAB' }}>
            {lastRefresh ? `Last updated ${lastRefresh.toLocaleTimeString()}` : ''}
          </p>
        </div>
        <button
          onClick={() => load()}
          disabled={loading}
          style={{
            padding: '9px 20px', borderRadius: '20px', border: 'none',
            background: loading ? '#B8D3CF' : '#1E6B68', color: '#fff',
            fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer',
          }}
        >
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {/* ── Users ── */}
      <SectionTitle>Users</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
        <StatCard label="Total users"   value={users?.total}     highlight />
        <StatCard label="Onboarded"     value={users?.onboarded} sub={`${users?.total ? Math.round(users.onboarded / users.total * 100) : 0}% of total`} />
        <StatCard label="Has voice"     value={users?.has_voice} sub="cloned voice ready" />
        <StatCard label="New (7d)"      value={users?.new_7d}    color="#2D8B87" highlight={users?.new_7d > 0} />
        <StatCard label="New (30d)"     value={users?.new_30d} />
      </div>

      {/* ── Engagement ── */}
      <SectionTitle>Engagement</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        <StatCard label="DAU"             value={engagement?.dau}          highlight color="#1E6B68" />
        <StatCard label="WAU"             value={engagement?.wau} />
        <StatCard label="MAU"             value={engagement?.mau} />
        <StatCard label="DAU/MAU"         value={engagement?.dau_mau_ratio != null ? `${engagement.dau_mau_ratio}%` : '—'} sub="Stickiness (target >20%)" color={engagement?.dau_mau_ratio >= 20 ? '#2D8B87' : '#B8906A'} highlight={engagement?.dau_mau_ratio >= 20} />
        <StatCard label="Sessions (7d)"   value={engagement?.sessions_7d} />
        <StatCard label="Sessions total"  value={engagement?.sessions_total} />
      </div>

      {/* Sessions per day chart */}
      {engagement?.sessions_by_day?.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 16px rgba(30,107,104,0.07)', marginBottom: '10px' }}>
          <div style={{ fontSize: '0.65rem', color: '#9AADAB', textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: '700', marginBottom: '16px' }}>
            Sessions per day (last 14 days)
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={engagement.sessions_by_day} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9AADAB' }} axisLine={false} tickLine={false} interval={1} />
              <YAxis tick={{ fontSize: 10, fill: '#9AADAB' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Sessions" fill="#2D8B87" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Retention ── */}
      <SectionTitle>Retention</SectionTitle>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '20px 24px', boxShadow: '0 2px 16px rgba(30,107,104,0.07)', marginBottom: '10px' }}>
        <RetentionBar label={`Day 7 retention  (cohort: ${retention?.cohort_7d_size ?? 0} users)`}  value={retention?.day_7}  target={30} />
        <RetentionBar label={`Day 30 retention (cohort: ${retention?.cohort_30d_size ?? 0} users)`} value={retention?.day_30} target={15} />
        <p style={{ margin: '12px 0 0', fontSize: '0.72rem', color: '#B8D3CF', fontStyle: 'italic' }}>
          Industry benchmarks: health apps Day 7 ~25–35%, Day 30 ~10–20%.
          HYPSEYA target: Day 7 &gt;30%, Day 30 &gt;20% (voice + ERI personalisation should outperform category).
        </p>
      </div>

      {/* ── ERI population ── */}
      <SectionTitle>ERI — Population</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        <StatCard label="Avg ERI"        value={eri?.avg}           highlight color="#2D8B87" />
        <StatCard label="Min ERI"        value={eri?.min} />
        <StatCard label="Max ERI"        value={eri?.max} />
        <StatCard label="Users tracked"  value={eri?.users_tracked} />
      </div>

      {/* ERI weekly trend */}
      {eri?.weekly_trend?.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 16px rgba(30,107,104,0.07)', marginBottom: '10px' }}>
          <div style={{ fontSize: '0.65rem', color: '#9AADAB', textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: '700', marginBottom: '16px' }}>
            Average ERI trend (last 8 weeks)
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={eri.weekly_trend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#9AADAB' }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#9AADAB' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={60} stroke="rgba(45,139,135,0.2)" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="avg_eri" name="Avg ERI" stroke="#2D8B87" strokeWidth={2} dot={{ r: 3, fill: '#2D8B87' }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Clinical + Content ── */}
      <SectionTitle>Clinical Outcomes</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        <StatCard label="Assessments done"     value={clinical?.total_assessments} />
        <StatCard label="Users with progress"  value={clinical?.users_with_progress} sub="2+ assessments" highlight={clinical?.users_with_progress > 0} />
      </div>

      <SectionTitle>Content</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
        <StatCard label="Meditations total"  value={content?.total_meditations} />
        <StatCard label="Audio ready"        value={content?.audio_ready} sub="synthesised" color="#2D8B87" highlight={content?.audio_ready > 0} />
        <StatCard label="Journal entries"    value={content?.total_journals} />
      </div>

      <p style={{ marginTop: '32px', textAlign: 'center', fontSize: '0.68rem', color: '#C8D8D6' }}>
        HYPSEYA Internal · {data?.generated_at ? new Date(data.generated_at).toLocaleString() : ''}
      </p>
    </div>
  );
}
